import IconButton from '@mui/material/IconButton';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import {
  convertDateToStringFormat,
  mapCraDaysManualEditEntryToAfterComponents,
  mapCraDaysManualEditEntryToBeforeComponents,
  totalWorkedDaysInCraFromManualEditComponents,
} from '@bloomays-lib/utils.shared';
import { CraDaysManualEditEntry } from '@bloomays-lib/types.shared';
import { useCraDaysManualEditHistory } from '../../hooks/useCraDaysManualEditHistory';
import type { CraDashboardRow } from '../../helpers/craDashboard';

export type CraDaysManualEditHistoryPanelProps = {
  row: CraDashboardRow;
  open: boolean;
  onBack: () => void;
};

type HistoryComponentRowSpec = {
  labelKey:
    | 'historyComponentWeekday'
    | 'historyComponentSaturday'
    | 'historyComponentSundayHoliday'
    | 'historyComponentAstreinte';
  beforeValue: (entry: CraDaysManualEditEntry) => number;
  afterValue: (entry: CraDaysManualEditEntry) => number;
};

const HISTORY_COMPONENT_ROWS: HistoryComponentRowSpec[] = [
  {
    labelKey: 'historyComponentWeekday',
    beforeValue: (entry) => entry.beforeNormalWorkedDays,
    afterValue: (entry) => entry.afterNormalWorkedDays,
  },
  {
    labelKey: 'historyComponentSaturday',
    beforeValue: (entry) => entry.beforeSaturdaysWorkedDays,
    afterValue: (entry) => entry.afterSaturdaysWorkedDays,
  },
  {
    labelKey: 'historyComponentSundayHoliday',
    beforeValue: (entry) => entry.beforeSundayAndHolidaysWorkedDays,
    afterValue: (entry) => entry.afterSundayAndHolidaysWorkedDays,
  },
  {
    labelKey: 'historyComponentAstreinte',
    beforeValue: (entry) => entry.beforeAstreinteWorkedDays,
    afterValue: (entry) => entry.afterAstreinteWorkedDays,
  },
];

const CraDaysManualEditHistoryPanel = ({ row, open, onBack }: CraDaysManualEditHistoryPanelProps) => {
  const { t } = useTranslation(['craDaysEdit']);
  const { entries, loading, error } = useCraDaysManualEditHistory({
    craAirtableId: row.activityId,
    enabled: open && (row.daysManuallyModified ?? false),
  });

  const orderedEntries = [...entries].reverse();

  return (
    <Panel>
      <Head>
        <BackRow>
          <IconButton aria-label={t('backToCraDetail', { ns: 'craDaysEdit' })} size="small" onClick={onBack}>
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <BackLabel type="button" onClick={onBack}>
            {t('backToCraDetail', { ns: 'craDaysEdit' })}
          </BackLabel>
        </BackRow>
      </Head>

      <Title>{t('historyDrawerTitle', { ns: 'craDaysEdit' })}</Title>
      <Subtitle>
        {row.bloomer || '—'} · {row.mission || '—'}
      </Subtitle>

      {loading ? <StatusText>{t('loadingHistory', { ns: 'craDaysEdit' })}</StatusText> : null}

      {!loading && error ? (
        <ErrorStatusText role="alert">{t('historyLoadError', { ns: 'craDaysEdit' })}</ErrorStatusText>
      ) : null}

      {!loading && !error && orderedEntries.length === 0 ? (
        <StatusText>{t('historyEmpty', { ns: 'craDaysEdit' })}</StatusText>
      ) : null}

      <EntriesStack>
        {orderedEntries.map((entry, index) => {
          const beforeTotal = totalWorkedDaysInCraFromManualEditComponents(
            mapCraDaysManualEditEntryToBeforeComponents(entry),
          );
          const afterTotal = totalWorkedDaysInCraFromManualEditComponents(
            mapCraDaysManualEditEntryToAfterComponents(entry),
          );
          const editedAtLabel = convertDateToStringFormat(new Date(entry.editedAt), 'dd/MM/yyyy HH:mm');

          return (
            <EntryCard key={`${entry.editedAt}-${index}`}>
              <EntryMeta>
                <EntryDate>{editedAtLabel}</EntryDate>
                <EntryAuthor>{t('historyEntryAuthor', { author: entry.editedBy, ns: 'craDaysEdit' })}</EntryAuthor>
              </EntryMeta>
              <EntryDays>
                {t('historyEntryTotal', { before: beforeTotal, after: afterTotal, ns: 'craDaysEdit' })}
              </EntryDays>
              <EntryDetail>
                {HISTORY_COMPONENT_ROWS.map((spec) => (
                  <EntryDetailLine key={spec.labelKey}>
                    {t('historyEntryComponentLine', {
                      label: t(spec.labelKey, { ns: 'craDaysEdit' }),
                      before: spec.beforeValue(entry),
                      after: spec.afterValue(entry),
                      ns: 'craDaysEdit',
                    })}
                  </EntryDetailLine>
                ))}
              </EntryDetail>
              <EntryReason>{entry.reason}</EntryReason>
            </EntryCard>
          );
        })}
      </EntriesStack>
    </Panel>
  );
};

export default CraDaysManualEditHistoryPanel;
export { CraDaysManualEditHistoryPanel };

const Panel = styled('div')(() => ({
  width: 'min(420px, 92vw)',
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
  padding: '20px 24px 28px',
}));

const Head = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
}));

const BackRow = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: 4,
}));

const BackLabel = styled('button')(({ theme }) => ({
  border: 'none',
  background: 'transparent',
  padding: 0,
  fontSize: 13,
  fontWeight: 600,
  fontFamily: '"Poppins Medium", sans-serif',
  color: theme.palette.blue[60],
  cursor: 'pointer',
  '&:hover': { textDecoration: 'underline' },
}));

const Title = styled('div')(({ theme }) => ({
  fontFamily: '"Poppins SemiBold", sans-serif',
  fontSize: 18,
  fontWeight: 700,
  color: theme.palette.neutral[90],
}));

const Subtitle = styled('div')(({ theme }) => ({
  fontSize: 13,
  color: theme.palette.neutral[60],
}));

const StatusText = styled('p')(({ theme }) => ({
  margin: 0,
  fontSize: 13,
  color: theme.palette.neutral[60],
}));

const ErrorStatusText = styled('p')(({ theme }) => ({
  margin: 0,
  fontSize: 13,
  fontWeight: 500,
  color: theme.palette.red[80],
}));

const EntriesStack = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
}));

const EntryCard = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  padding: '12px 14px',
  borderRadius: 8,
  backgroundColor: theme.palette.neutral[10],
  border: `1px solid ${theme.palette.neutral[15]}`,
}));

const EntryMeta = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
}));

const EntryDate = styled('div')(({ theme }) => ({
  fontSize: 12,
  fontWeight: 600,
  color: theme.palette.neutral[70],
}));

const EntryAuthor = styled('div')(({ theme }) => ({
  fontSize: 12,
  color: theme.palette.neutral[60],
}));

const EntryDays = styled('div')(({ theme }) => ({
  fontSize: 14,
  fontWeight: 700,
  color: theme.palette.neutral[90],
}));

const EntryDetail = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  fontSize: 12,
  color: theme.palette.neutral[70],
}));

const EntryDetailLine = styled('div')(() => ({
  lineHeight: 1.35,
}));

const EntryReason = styled('div')(({ theme }) => ({
  fontSize: 13,
  color: theme.palette.neutral[80],
  wordBreak: 'break-word',
}));
